import type { LoMasVendidoQueryFilters } from '@/app/lib/api/api'
import { readSearchParam, type SearchParamsInput } from '@/app/lib/utils'

/**
 * Mapa campo→parámetro legible de la URL (sin sufijos técnicos como
 * `_nicename` — FR-019). Única fuente de verdad compartida entre el parseo
 * server-side (`page.tsx`) y la actualización de la URL en cliente
 * (`useListingFieldFilters`, inyectado desde `LoMasVendidoFilters`). Ver
 * contracts/data-lo-mas-vendido.md. Precio pasó de un `min_price` de tramo
 * único a un rango continuo (revisado — mismo mecanismo que Promociones):
 * `priceMin`/`priceMax` viajan como `price_min`/`price_max`; `freeFilter`
 * (checkbox "Mostrar solo gratuitos") como `free_filter`.
 */
export const READABLE_FILTER_PARAMS = {
  genre: 'genre',
  subgenre: 'subgenre',
  dateRange: 'date_range',
  priceMin: 'price_min',
  priceMax: 'price_max',
  freeFilter: 'free_filter',
} as const

function validatePriceValue(raw: string | undefined): number | undefined {
  if (!raw) return undefined
  const value = Number(raw)
  return Number.isNaN(value) ? undefined : value
}

/**
 * `searchParams` de Next.js (query legible de la URL) → filtros reales que
 * espera `getLoMasVendidoPage` (FR-003). Los parámetros ausentes o vacíos se
 * omiten (no se envían a `/data`).
 */
export function parseLoMasVendidoFilters(searchParams: SearchParamsInput): LoMasVendidoQueryFilters {
  const genre = readSearchParam(searchParams, READABLE_FILTER_PARAMS.genre)
  const subgenre = readSearchParam(searchParams, READABLE_FILTER_PARAMS.subgenre)
  const dateRange = readSearchParam(searchParams, READABLE_FILTER_PARAMS.dateRange)
  const priceMin = validatePriceValue(readSearchParam(searchParams, READABLE_FILTER_PARAMS.priceMin))
  const priceMax = validatePriceValue(readSearchParam(searchParams, READABLE_FILTER_PARAMS.priceMax))
  const freeFilter = readSearchParam(searchParams, READABLE_FILTER_PARAMS.freeFilter) === 'on'

  const filters: LoMasVendidoQueryFilters = {}
  if (genre) filters.genre = genre
  if (subgenre) filters.subgenre = subgenre
  if (dateRange) filters.dateRange = dateRange
  if (priceMin !== undefined) filters.priceMin = priceMin
  if (priceMax !== undefined) filters.priceMax = priceMax
  if (freeFilter) filters.freeFilter = true
  return filters
}
